import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet } from '@/lib/api/client';
import type { WorkOrder, Mold } from '@/types/maintenance';

// ─── Period helpers (kept for component use) ──────────────────────────────────

export type PeriodMode = 'week' | 'month' | 'year' | 'custom';

export interface PeriodRange {
  start: Date;
  end: Date;
}

export function getPeriodBoundaries(
  mode: PeriodMode,
  customFrom?: string,
  customTo?: string,
): PeriodRange {
  if (mode === 'custom' && customFrom) {
    return {
      start: new Date(customFrom + 'T00:00:00'),
      end: customTo ? new Date(customTo + 'T23:59:59') : new Date(),
    };
  }
  const end = new Date();
  const start = new Date();
  if (mode === 'week') start.setDate(start.getDate() - 7);
  else if (mode === 'month') start.setMonth(start.getMonth() - 1);
  else start.setFullYear(start.getFullYear() - 1);
  return { start, end };
}

export function getPreviousPeriod(range: PeriodRange): PeriodRange {
  const duration = range.end.getTime() - range.start.getTime();
  return {
    start: new Date(range.start.getTime() - duration),
    end: new Date(range.start.getTime()),
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const FAILURE_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--status-amber))',
  'hsl(var(--status-red))',
  'hsl(var(--status-green))',
  '#8884d8',
  '#a3c4f3',
];

// ─── Return types ─────────────────────────────────────────────────────────────

export interface KPIValues {
  totalWOs: number;
  totalWOsPrev: number;
  completedWOs: number;
  completedWOsPrev: number;
  completionRate: number;
  completionRatePrev: number;
  avgMTTRHours: number | null;
  avgMTTRHoursPrev: number | null;
  pmCompliance: number | null;
  pmCompliancePrev: number | null;
  chronicMoldsCount: number;
  chronicMoldsCountPrev: number;
}

export interface WOTrendBucket {
  label: string;
  opened: number;
  closed: number;
}

export interface PMBreakdown {
  compliance: number | null;
  onTimePct: number | null;
  completed: number;
  late: number;
  pending: number;
  total: number;
}

export interface MTTRData {
  avgHours: number | null;
  medianHours: number | null;
  p90Hours: number | null;
  byMold: Array<{ mold: Mold; avgHours: number; count: number }>;
}

export interface MIBFData {
  avgHours: number | null;
  ratio: number | null;
  label: 'good' | 'at-risk' | 'critical' | null;
}

export interface MCBFData {
  fleetMcbf: number | null;
  rows: Array<{ mold: Mold; mcbf: number; failureCount: number; trend: 'up' | 'down' | 'stable' }>;
}

export interface FailureCategoryItem {
  category: string;
  count: number;
  pct: number;
  color: string;
}

export interface BlockedTimeData {
  byReason: Array<{ reason: string; hours: number }>;
  totalHours: number;
  capacityPct: number;
  teamSize: number;
  workingDays: number;
}

export interface ChronicMoldRow {
  mold: Mold;
  count: number;
  avgMTTRHours: number;
  totalLaborCost: number;
  totalPartsCost: number;
  lastFailure: string;
  status: 'critical' | 'watch';
}

export interface CostData {
  laborCost: number;
  partsCost: number;
  totalCost: number;
  costPerWO: number | null;
  prevTotalCost: number;
  breakdown: Array<{ name: string; cost: number | null }>;
}

export interface MaintenanceKPIResult {
  kpis: KPIValues;
  woTrend: WOTrendBucket[];
  pmBreakdown: PMBreakdown;
  mttrData: MTTRData;
  mitAvgMinutes: number | null;
  mibfData: MIBFData;
  mcbfData: MCBFData;
  failureCategories: FailureCategoryItem[];
  rootCauseCategories: FailureCategoryItem[];
  failureByCategory: Array<{ category: string; count: number }>;
  woTypeBreakdown: Array<{ type: string; label: string; count: number }>;
  blockedTime: BlockedTimeData;
  chronicMolds: ChronicMoldRow[];
  costs: CostData | null;
  recentWOs: WorkOrder[];
  periodRange: PeriodRange;
  prevPeriodRange: PeriodRange;
  isLoading: boolean;
  error: string | null;
}

// ─── Empty defaults (shown while loading) ────────────────────────────────────

const EMPTY_KPIS: KPIValues = {
  totalWOs: 0, totalWOsPrev: 0, completedWOs: 0, completedWOsPrev: 0,
  completionRate: 0, completionRatePrev: 0,
  avgMTTRHours: null, avgMTTRHoursPrev: null,
  pmCompliance: null, pmCompliancePrev: null,
  chronicMoldsCount: 0, chronicMoldsCountPrev: 0,
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseMaintenanceKPIsInput {
  period: PeriodMode;
  orgId: string | null;
  customFrom?: string;
  customTo?: string;
  moldId?: string;
  technicianId?: string;
}

export function useMaintenanceKPIs({
  period,
  orgId,
  customFrom,
  customTo,
  moldId,
  technicianId,
}: UseMaintenanceKPIsInput): MaintenanceKPIResult {
  const { user } = useAuth();

  const params = new URLSearchParams({ period });
  if (customFrom) params.set('from', customFrom);
  if (customTo) params.set('to', customTo);
  if (moldId) params.set('mold_id', moldId);
  if (technicianId) params.set('assigned_to', technicianId);

  const { data, isLoading, error } = useQuery<MaintenanceKPIResult & { periodRange: { start: string; end: string }; prevPeriodRange: { start: string; end: string } }>({
    queryKey: ['reports_kpis', orgId, period, customFrom, customTo, moldId, technicianId],
    queryFn: () => apiGet(`/api/reports/kpis?${params.toString()}`),
    staleTime: 5 * 60_000,
    enabled: !!user,
  });

  const periodRange = useMemo<PeriodRange>(() => {
    if (!data) return getPeriodBoundaries(period, customFrom, customTo);
    return { start: new Date(data.periodRange.start), end: new Date(data.periodRange.end) };
  }, [data, period, customFrom, customTo]);

  const prevPeriodRange = useMemo<PeriodRange>(() => {
    if (!data) return getPreviousPeriod(periodRange);
    return { start: new Date(data.prevPeriodRange.start), end: new Date(data.prevPeriodRange.end) };
  }, [data, periodRange]);

  if (!data) {
    return {
      kpis: EMPTY_KPIS,
      woTrend: [],
      pmBreakdown: { compliance: null, onTimePct: null, completed: 0, late: 0, pending: 0, total: 0 },
      mttrData: { avgHours: null, medianHours: null, p90Hours: null, byMold: [] },
      mitAvgMinutes: null,
      mibfData: { avgHours: null, ratio: null, label: null },
      mcbfData: { rows: [], fleetMcbf: null },
      failureCategories: [],
      rootCauseCategories: [],
      failureByCategory: [],
      woTypeBreakdown: [],
      blockedTime: { byReason: [], totalHours: 0, capacityPct: 0, teamSize: 4, workingDays: 0 },
      chronicMolds: [],
      costs: null,
      recentWOs: [],
      periodRange,
      prevPeriodRange,
      isLoading,
      error: error ? (error as Error).message : null,
    };
  }

  const { periodRange: _pr, prevPeriodRange: _ppr, ...rest } = data;
  return {
    ...rest,
    periodRange,
    prevPeriodRange,
    isLoading,
    error: null,
  };
}
